import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Trash2, Plus, Pencil, X, Save, Loader2, ExternalLink } from "lucide-react";
import { CATEGORIES } from "../../data/catalog";
import FileUpload, { absolutize } from "../../components/admin/FileUpload";
import { PromoPopupPreview } from "../../components/home/PromoPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------------------------------------------------------------
// Homepage editor (hero + categories + trending + occasions + FPM options)
// -------------------------------------------------------------

const HOME_SECTIONS = [
  {
    key: "hero",
    label: "Hero Banner",
    description: "The big banner at the top of the homepage.",
  },
  {
    key: "categories",
    label: "Shop by Categories",
    description: "The row of circular tiles (Earrings, Rings, ...).",
    fields: ["label", "image", "link"],
  },
  {
    key: "trending",
    label: "Trending Now",
    description: "Cards with optional NEW / HOT badge.",
    fields: ["label", "sub", "image", "link", "tag"],
  },
  {
    key: "occasions",
    label: "Shop by Occasion",
    description: "Large image tiles (Wedding, Festivals, ...).",
    fields: ["label", "sub", "image", "link"],
  },
  {
    key: "looks",
    label: "Choose Your Look",
    description: "Full-model shots (or short videos) for the swiper. 4–5 works best.",
    fields: ["label", "image", "video", "poster", "link"],
  },
  {
    key: "fpm_metal",
    label: "Find Match: By Metal",
    description: "Options under the 'By Metal' picker.",
    fields: ["label", "link"],
    section: "fpm_option",
    parent: "metal",
  },
  {
    key: "fpm_occasion",
    label: "Find Match: By Occasion",
    description: "Options under the 'By Occasion' picker.",
    fields: ["label", "link"],
    section: "fpm_option",
    parent: "occasion",
  },
  {
    key: "fpm_style",
    label: "Find Match: By Style",
    description: "Options under the 'By Style' picker.",
    fields: ["label", "link"],
    section: "fpm_option",
    parent: "style",
  },
];

function HomepageTab() {
  const [activeKey, setActiveKey] = useState("hero");
  const active = HOME_SECTIONS.find((s) => s.key === activeKey);

  return (
    <div>
      <h1 className="admin-h1">Homepage</h1>
      <p className="admin-sub">
        Edit every section of your public homepage — banner, tiles, categories,
        picker options — no code needed.
      </p>

      <div className="admin-subtabs" data-testid="home-subtabs">
        {HOME_SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={activeKey === s.key ? "on" : ""}
            onClick={() => setActiveKey(s.key)}
            data-testid={`home-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {active && active.key === "hero" && <HeroEditor />}
      {active && active.key !== "hero" && (
        <HomeItemsEditor
          section={active.section || active.key}
          parent={active.parent}
          title={active.label}
          description={active.description}
          fields={active.fields}
          storageKey={active.key}
        />
      )}
    </div>
  );
}

function HeroEditor() {
  const empty = {
    eyebrow: "",
    titleLine1: "",
    titleLine2: "",
    lead: "",
    ctaPrimaryLabel: "",
    ctaPrimaryLink: "",
    ctaSecondaryLabel: "",
    ctaSecondaryLink: "",
    mainImage: "",
    productImage: "",
    productName: "",
    logoUrl: "",
    craftsmanshipVideo: "",
    craftsmanshipPoster: "",
    heroVideo: "",
    heroVideoPoster: "",
    logoWidth: 64,
    textLogoImage: "",
    subNavColor: "#2B1810",
    subNavPhoneColor: "#F3D9A8",
    subNavAnimation: true,
    navOpacity: 55,
    subNavOpacity: 32,
    bannerImage: "",
    bannerEnabled: false,
    bannerMode: "image",
    bannerHeading: "",
    bannerBody: "",
    bannerBgColor: "#1c110a",
    bannerTextColor: "#F7F0E4",
    bannerButtonEnabled: true,
    bannerButtonLabel: "",
    bannerButtonLink: "",
    bannerLink: "",
    loginTitle: "",
    loginSubtitle: "",
    loginTagline: "",
    loginLogoImage: "",
    loginGradientFrom: "#2B1810",
    loginGradientTo: "#8B5A2B",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/hero`)
      .then((r) => {
        if (r.data && Object.keys(r.data).length) {
          setForm({ ...empty, ...r.data });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setOk("");
    try {
      await axios.put(`${API}/admin/hero`, form);
      setOk("Hero updated ✓ (refresh homepage to see changes)");
      setTimeout(() => setOk(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-card">
      <h3>Hero Banner</h3>
      <p className="admin-muted">
        These are the big words and images on the top of your homepage. Leave any
        field blank to keep its current default.
      </p>
      <form onSubmit={submit} className="admin-form">
        <label>
          <span>Small eyebrow text</span>
          <input
            value={form.eyebrow || ""}
            onChange={(e) => update("eyebrow", e.target.value)}
            placeholder="e.g. The Alankar House · Since 1994"
            data-testid="hero-eyebrow"
          />
        </label>
        <div className="admin-row two">
          <label>
            <span>Title line 1</span>
            <input
              value={form.titleLine1 || ""}
              onChange={(e) => update("titleLine1", e.target.value)}
              placeholder="Every ornament"
              data-testid="hero-title-1"
            />
          </label>
          <label>
            <span>Title line 2 (italic)</span>
            <input
              value={form.titleLine2 || ""}
              onChange={(e) => update("titleLine2", e.target.value)}
              placeholder="tells a story."
              data-testid="hero-title-2"
            />
          </label>
        </div>
        <label>
          <span>Lead paragraph</span>
          <textarea
            rows={3}
            value={form.lead || ""}
            onChange={(e) => update("lead", e.target.value)}
            placeholder="One-liner about your brand…"
          />
        </label>
        <div className="admin-row two">
          <label>
            <span>Primary button label</span>
            <input
              value={form.ctaPrimaryLabel || ""}
              onChange={(e) => update("ctaPrimaryLabel", e.target.value)}
              placeholder="Shop Collection"
            />
          </label>
          <label>
            <span>Primary button link</span>
            <input
              value={form.ctaPrimaryLink || ""}
              onChange={(e) => update("ctaPrimaryLink", e.target.value)}
              placeholder="/collections/gold"
            />
          </label>
        </div>
        <div className="admin-row two">
          <label>
            <span>Secondary button label</span>
            <input
              value={form.ctaSecondaryLabel || ""}
              onChange={(e) => update("ctaSecondaryLabel", e.target.value)}
              placeholder="Explore Diamond"
            />
          </label>
          <label>
            <span>Secondary button link</span>
            <input
              value={form.ctaSecondaryLink || ""}
              onChange={(e) => update("ctaSecondaryLink", e.target.value)}
              placeholder="/collections/diamond"
            />
          </label>
        </div>
        <div className="admin-row two">
          <FileUpload
            label="Main image (model shot)"
            value={form.mainImage || ""}
            onChange={(u) => update("mainImage", u)}
            accept="image/*"
            kind="image"
          />
          <FileUpload
            label="Featured product image"
            value={form.productImage || ""}
            onChange={(u) => update("productImage", u)}
            accept="image/*"
            kind="image"
          />
        </div>
        <label>
          <span>Featured product name</span>
          <input
            value={form.productName || ""}
            onChange={(e) => update("productName", e.target.value)}
            placeholder="e.g. Dazzling Teardrop Gold Jhumkas"
          />
        </label>

        <h4 style={{ marginTop: 24, fontFamily: "'Playfair Display', serif", fontSize: 22, color: "var(--copper-deep)" }}>
          Full-screen hero video
        </h4>
        <p className="admin-muted" style={{ marginTop: -8 }}>
          Plays automatically on the homepage, covering the navbar. Keep it under
          ~30 seconds for the fastest load. Any format (MOV / HEVC / MP4) — we
          auto-optimise on upload.
        </p>
        <div className="admin-row two">
          <FileUpload
            label="Hero video"
            value={form.heroVideo || ""}
            onChange={(u) => update("heroVideo", u)}
            accept="video/*"
            kind="video"
          />
          <FileUpload
            label="Hero video poster (fallback image)"
            value={form.heroVideoPoster || ""}
            onChange={(u) => update("heroVideoPoster", u)}
            accept="image/*"
            kind="image"
          />
        </div>

        <h4 style={{ marginTop: 24, fontFamily: "'Playfair Display', serif", fontSize: 22, color: "var(--copper-deep)" }}>
          Brand &amp; workshop
        </h4>
        <FileUpload
          label="Logo image (shown in the header)"
          value={form.logoUrl || ""}
          onChange={(u) => update("logoUrl", u)}
          accept="image/*"
          kind="image"
        />
        <div className="admin-row two">
          <label>
            <span>Logo width in the header (px)</span>
            <input
              type="number"
              min={24}
              max={220}
              step={2}
              value={form.logoWidth || 64}
              onChange={(e) => update("logoWidth", Number(e.target.value) || 64)}
              placeholder="64"
              data-testid="hero-logo-width"
            />
          </label>
          <FileUpload
            label="Text-logo PNG (used where the brand name appears)"
            value={form.textLogoImage || ""}
            onChange={(u) => update("textLogoImage", u)}
            accept="image/*"
            kind="image"
          />
        </div>
        <div className="admin-row two">
          <label>
            <span>Sub-navbar colour</span>
            <input
              type="color"
              value={form.subNavColor || "#2B1810"}
              onChange={(e) => update("subNavColor", e.target.value)}
              data-testid="hero-subnav-color"
              style={{ height: 42, padding: 4 }}
            />
          </label>
          <label>
            <span>Sub-navbar "Call: number" colour</span>
            <input
              type="color"
              value={form.subNavPhoneColor || "#F3D9A8"}
              onChange={(e) => update("subNavPhoneColor", e.target.value)}
              data-testid="hero-subnav-phone-color"
              style={{ height: 42, padding: 4 }}
            />
          </label>
        </div>
        <div className="admin-row two">
          <label
            className="admin-check"
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <input
              type="checkbox"
              checked={form.subNavAnimation !== false}
              onChange={(e) => update("subNavAnimation", e.target.checked)}
              data-testid="hero-subnav-anim"
            />
            <span>Shimmer animation on sub-navbar</span>
          </label>
        </div>
        <div className="admin-row two">
          <label>
            <span>
              Navbar transparency: <strong>{form.navOpacity ?? 55}%</strong>
              <em style={{ color: "#8A5427", fontStyle: "normal", marginLeft: 8, fontSize: 12 }}>
                0 = see-through, 100 = solid
              </em>
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={form.navOpacity ?? 55}
              onChange={(e) => update("navOpacity", Number(e.target.value))}
              data-testid="hero-nav-opacity"
              style={{ width: "100%" }}
            />
          </label>
          <label>
            <span>
              Sub-navbar transparency: <strong>{form.subNavOpacity ?? 32}%</strong>
              <em style={{ color: "#8A5427", fontStyle: "normal", marginLeft: 8, fontSize: 12 }}>
                blends the chosen colour with the video
              </em>
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={form.subNavOpacity ?? 32}
              onChange={(e) => update("subNavOpacity", Number(e.target.value))}
              data-testid="hero-subnav-opacity"
              style={{ width: "100%" }}
            />
          </label>
        </div>
        <div className="admin-row two">
          <FileUpload
            label="Workshop video (Alankar Workshop section)"
            value={form.craftsmanshipVideo || ""}
            onChange={(u) => update("craftsmanshipVideo", u)}
            accept="video/*"
            kind="video"
          />
          <FileUpload
            label="Video poster / thumbnail"
            value={form.craftsmanshipPoster || ""}
            onChange={(u) => update("craftsmanshipPoster", u)}
            accept="image/*"
            kind="image"
          />
        </div>

        <h4 style={{ marginTop: 24, fontFamily: "'Playfair Display', serif", fontSize: 22, color: "var(--copper-deep)" }}>
          Promo popup banner
        </h4>
        <p className="admin-muted" style={{ marginTop: -8 }}>
          A center-of-screen popup shown the first time each new visitor opens
          your website. Perfect for sales, festival launches or new arrivals.
          Choose either <strong>Image mode</strong> (upload your own creative)
          or <strong>Text mode</strong> (headline + description with a button).
          The popup auto re-appears whenever you change anything below, so
          every promotion reaches every returning customer at least once.
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            margin: "8px 0 6px",
          }}
        >
          <label
            className="admin-check"
            style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}
          >
            <input
              type="checkbox"
              checked={!!form.bannerEnabled}
              onChange={(e) => update("bannerEnabled", e.target.checked)}
              data-testid="hero-banner-enabled"
            />
            <span>Show popup on the homepage</span>
          </label>
          <button
            type="button"
            className="btn btn-outline"
            onClick={(e) => {
              e.preventDefault();
              setShowPreview(true);
            }}
            data-testid="hero-banner-preview"
            style={{ padding: "8px 18px", fontSize: 13 }}
          >
            Preview popup
          </button>
        </div>

        <div className="admin-row two" style={{ marginTop: 6 }}>
          <label className="admin-check" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="radio"
              name="bannerMode"
              value="image"
              checked={form.bannerMode !== "text"}
              onChange={() => update("bannerMode", "image")}
              data-testid="hero-banner-mode-image"
            />
            <span>Image mode — upload your own creative</span>
          </label>
          <label className="admin-check" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="radio"
              name="bannerMode"
              value="text"
              checked={form.bannerMode === "text"}
              onChange={() => update("bannerMode", "text")}
              data-testid="hero-banner-mode-text"
            />
            <span>Text mode — headline + description</span>
          </label>
        </div>

        {form.bannerMode !== "text" ? (
          <div style={{ marginTop: 12 }}>
            <FileUpload
              label="Popup image (any size — auto scales)"
              value={form.bannerImage || ""}
              onChange={(u) => update("bannerImage", u)}
              accept="image/*"
              kind="image"
            />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <label>
              <span>Headline</span>
              <input
                value={form.bannerHeading || ""}
                onChange={(e) => update("bannerHeading", e.target.value)}
                placeholder="Festive Diwali Collection"
                data-testid="hero-banner-heading"
              />
            </label>
            <label>
              <span>Short description</span>
              <textarea
                rows={3}
                value={form.bannerBody || ""}
                onChange={(e) => update("bannerBody", e.target.value)}
                placeholder="Handcrafted 22-karat gold sets — book a preview at our Rasta Peth showroom."
                data-testid="hero-banner-body"
              />
            </label>
            <div className="admin-row two">
              <label>
                <span>Background colour</span>
                <input
                  type="color"
                  value={form.bannerBgColor || "#1c110a"}
                  onChange={(e) => update("bannerBgColor", e.target.value)}
                  data-testid="hero-banner-bg"
                  style={{ height: 42, padding: 4 }}
                />
              </label>
              <label>
                <span>Text colour</span>
                <input
                  type="color"
                  value={form.bannerTextColor || "#F7F0E4"}
                  onChange={(e) => update("bannerTextColor", e.target.value)}
                  data-testid="hero-banner-text-color"
                  style={{ height: 42, padding: 4 }}
                />
              </label>
            </div>
          </div>
        )}

        <label
          className="admin-check"
          style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0 6px" }}
        >
          <input
            type="checkbox"
            checked={form.bannerButtonEnabled !== false}
            onChange={(e) => update("bannerButtonEnabled", e.target.checked)}
            data-testid="hero-banner-button-enabled"
          />
          <span>Show a call-to-action button on the popup</span>
        </label>
        <div className="admin-row two">
          <label>
            <span>Button label</span>
            <input
              value={form.bannerButtonLabel || ""}
              onChange={(e) => update("bannerButtonLabel", e.target.value)}
              placeholder="Shop Now"
              data-testid="hero-banner-button-label"
            />
          </label>
          <label>
            <span>Button link (where it goes when clicked)</span>
            <input
              value={form.bannerButtonLink || ""}
              onChange={(e) => update("bannerButtonLink", e.target.value)}
              placeholder="/collections/gold or https://wa.me/919503650463"
              data-testid="hero-banner-button-link"
            />
          </label>
        </div>
        <p className="admin-muted" style={{ marginTop: 0, fontSize: 12 }}>
          Tip: leave the button off if your uploaded image already has a
          call-to-action baked into the design. For image mode without a
          button, the whole image becomes clickable if you fill in the
          legacy field below.
        </p>
        <label style={{ marginTop: 4 }}>
          <span>Whole-image click link (image mode only, optional)</span>
          <input
            value={form.bannerLink || ""}
            onChange={(e) => update("bannerLink", e.target.value)}
            placeholder="/collections/diamond   (leave blank for no link)"
            data-testid="hero-banner-link"
          />
        </label>

        <h4 style={{ marginTop: 24, fontFamily: "'Playfair Display', serif", fontSize: 22, color: "var(--copper-deep)" }}>
          Login &amp; sign-up page
        </h4>
        <p className="admin-muted" style={{ marginTop: -8 }}>
          Everything customers see on the sign-in page — your welcome message,
          the gradient behind it, the brand tagline and an optional logo image.
          Leave any field blank to keep the current default.
        </p>
        <label>
          <span>Big welcome headline</span>
          <input
            value={form.loginTitle || ""}
            onChange={(e) => update("loginTitle", e.target.value)}
            placeholder="Welcome to Alankar."
            data-testid="hero-login-title"
          />
        </label>
        <label>
          <span>Subtitle paragraph</span>
          <textarea
            rows={3}
            value={form.loginSubtitle || ""}
            onChange={(e) => update("loginSubtitle", e.target.value)}
            placeholder="Sign in to explore over 1,000 designs, save your favourites and get a personalised WhatsApp enquiry with one tap."
            data-testid="hero-login-subtitle"
          />
        </label>
        <label>
          <span>Tagline at the bottom</span>
          <input
            value={form.loginTagline || ""}
            onChange={(e) => update("loginTagline", e.target.value)}
            placeholder="30+ Years of Trust · Rasta Peth, Pune"
            data-testid="hero-login-tagline"
          />
        </label>
        <div className="admin-row two">
          <label>
            <span>Gradient — starting colour (top-left)</span>
            <input
              type="color"
              value={form.loginGradientFrom || "#2B1810"}
              onChange={(e) => update("loginGradientFrom", e.target.value)}
              data-testid="hero-login-grad-from"
              style={{ height: 42, padding: 4 }}
            />
          </label>
          <label>
            <span>Gradient — ending colour (bottom-right)</span>
            <input
              type="color"
              value={form.loginGradientTo || "#8B5A2B"}
              onChange={(e) => update("loginGradientTo", e.target.value)}
              data-testid="hero-login-grad-to"
              style={{ height: 42, padding: 4 }}
            />
          </label>
        </div>
        <FileUpload
          label="Optional logo image (replaces the wordmark on the login page)"
          value={form.loginLogoImage || ""}
          onChange={(u) => update("loginLogoImage", u)}
          accept="image/*"
          kind="image"
        />
        {ok && <div className="admin-ok">{ok}</div>}
        <button type="submit" className="btn btn-primary" disabled={saving} data-testid="hero-save">
          <Save size={16} /> {saving ? "Saving…" : "Save Hero"}
        </button>
      </form>
      {showPreview ? (
        <PromoPopupPreview form={form} onClose={() => setShowPreview(false)} />
      ) : null}
    </div>
  );
}

const EMPTY_HOME_ITEM = {
  label: "",
  sub: "",
  image: "",
  video: "",
  poster: "",
  heroImage: "",
  link: "/",
  tag: "",
  order: 0,
};

function HomeItemsEditor({ section, parent, title, description, fields, storageKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_HOME_ITEM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState("");

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/home-items`, { params: { section } })
      .then((r) => {
        const rows = parent
          ? r.data.filter((x) => x.parent === parent)
          : r.data;
        setItems(rows);
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [section, parent, storageKey]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      label: it.label || "",
      sub: it.sub || "",
      image: it.image || "",
      video: it.video || "",
      poster: it.poster || "",
      heroImage: it.heroImage || "",
      link: it.link || "/",
      tag: it.tag || "",
      order: Number(it.order) || 0,
    });
    setOk("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_HOME_ITEM);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setSaving(true);
    try {
      const payload = {
        section,
        parent: parent || null,
        label: form.label,
        sub: form.sub || "",
        image: form.image || "",
        video: form.video || "",
        poster: form.poster || "",
        heroImage: form.heroImage || "",
        link: form.link || "/",
        tag: form.tag || "",
        order: Number(form.order) || 0,
      };
      if (editingId) {
        await axios.put(`${API}/admin/home-items/${editingId}`, payload);
        setOk("Saved ✓");
        setEditingId(null);
      } else {
        await axios.post(`${API}/admin/home-items`, payload);
        setOk("Added ✓");
      }
      setForm(EMPTY_HOME_ITEM);
      load();
      setTimeout(() => setOk(""), 2500);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/home-items/${id}`);
      if (editingId === id) cancelEdit();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      alert(
        `Could not delete this item.\n${detail || err.message || ""}${
          status ? ` (HTTP ${status})` : ""
        }`
      );
    }
  };

  const showField = (name) => (fields ? fields.includes(name) : true);

  return (
    <>
      <div className="admin-card">
        <h3>{editingId ? `Editing item` : `Add ${title.replace(/^Find Match: /, "")}`}</h3>
        <p className="admin-muted">{description}</p>
        <form onSubmit={submit} className="admin-form">
          <div className="admin-row two">
            {showField("label") && (
              <label>
                <span>Label *</span>
                <input
                  value={form.label}
                  onChange={(e) => update("label", e.target.value)}
                  placeholder="e.g. Wedding"
                  required
                  data-testid={`home-${storageKey}-label`}
                />
              </label>
            )}
            {showField("link") && (
              <label>
                <span>Link (where it should go)</span>
                <input
                  value={form.link}
                  onChange={(e) => update("link", e.target.value)}
                  placeholder="/collections/gold  or  /wishlist"
                  data-testid={`home-${storageKey}-link`}
                />
              </label>
            )}
          </div>
          {showField("sub") && (
            <label>
              <span>Sub-text / caption</span>
              <input
                value={form.sub}
                onChange={(e) => update("sub", e.target.value)}
                placeholder="e.g. Bridal sets that become heirlooms"
              />
            </label>
          )}
          <div className="admin-row two">
            {showField("tag") && (
              <label>
                <span>Badge tag (optional)</span>
                <input
                  value={form.tag}
                  onChange={(e) => update("tag", e.target.value)}
                  placeholder="NEW / HOT / SALE"
                />
              </label>
            )}
            <label>
              <span>Sort order (lower shows first)</span>
              <input
                type="number"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
              />
            </label>
          </div>
          {showField("image") && (
            <FileUpload
              label={
                section === "categories"
                  ? "Thumbnail image"
                  : section === "trending"
                  ? "Thumbnail image (shown when the video isn't playing / on mobile)"
                  : section === "looks"
                  ? "Thumbnail image (shown when the video isn't playing / on mobile)"
                  : "Image"
              }
              value={form.image}
              onChange={(u) => update("image", u)}
              accept="image/*"
              kind="image"
            />
          )}
          {(section === "trending" || section === "looks") && (
            <div className="admin-row two">
              <FileUpload
                label="Card video (MP4 / MOV — auto-optimised to H.264)"
                value={form.video}
                onChange={(u) => update("video", u)}
                accept="video/*"
                kind="video"
              />
              <FileUpload
                label="Video poster fallback (still frame shown before the video buffers)"
                value={form.poster}
                onChange={(u) => update("poster", u)}
                accept="image/*"
                kind="image"
              />
            </div>
          )}
          {section === "categories" && (
            <FileUpload
              label="Category page banner (large hero image at the top of that category)"
              value={form.heroImage}
              onChange={(u) => update("heroImage", u)}
              accept="image/*"
              kind="image"
            />
          )}
          {ok && <div className="admin-ok">{ok}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid={`home-${storageKey}-save`}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="fu-spin" /> Saving…
                </>
              ) : editingId ? (
                <>
                  <Save size={16} /> Save Changes
                </>
              ) : (
                <>
                  <Plus size={16} /> Add
                </>
              )}
            </button>
            {editingId && (
              <button type="button" className="btn btn-outline" onClick={cancelEdit}>
                <X size={16} /> Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3>
          Current items <span className="admin-count">({items.length})</span>
        </h3>
        {loading ? (
          <p className="admin-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="admin-muted">
            Nothing added yet — until you add items here, the site shows its built-in defaults.
          </p>
        ) : (
          <div className="admin-list">
            {items.map((it) => (
              <div
                className={`admin-list-item ${editingId === it.id ? "editing" : ""}`}
                key={it.id}
              >
                <div className="admin-list-thumb">
                  {it.image ? <img src={absolutize(it.image)} alt={it.label} /> : null}
                </div>
                <div className="admin-list-body">
                  <strong>{it.label}</strong>
                  <span>
                    {it.sub ? `${it.sub} · ` : ""}
                    {it.link}
                    {it.tag ? ` · ${it.tag}` : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className="admin-edit"
                  onClick={() => startEdit(it)}
                  data-testid={`home-${storageKey}-edit-${it.id}`}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="admin-del"
                  onClick={() => del(it.id)}
                  data-testid={`home-${storageKey}-delete-${it.id}`}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default HomepageTab;
